import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronUp } from 'lucide-react';
import DataTable from '../dashboard/DataTable';
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const BreakdownCard = ({
  title,
  data,
  expanded,
  onToggleExpand,
  chartType = 'pie', // 'pie' or 'bar'
  limit = 5
}) => {
  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">No data available</p>
        </CardContent>
      </Card>
    );
  }

  // Normalize data structure - handle both 'revenue' and 'total_revenue' field names
  const normalizedData = data.map(item => ({
    ...item,
    revenue: item.revenue ?? item.total_revenue ?? 0,
    revenue_percent: item.revenue_percent ?? 0, // Will be calculated if not present
    impressions: item.impressions ?? item.total_impressions ?? 0,
    ecpm: item.ecpm ?? item.average_ecpm ?? 0,
  }));

  // Calculate revenue_percent if not present
  const totalRevenue = normalizedData.reduce((sum, item) => sum + (item.revenue || 0), 0);
  const dataWithPercent = normalizedData.map(item => ({
    ...item,
    revenue_percent: item.revenue_percent || (totalRevenue > 0 ? (item.revenue / totalRevenue) * 100 : 0),
  }));

  const displayData = dataWithPercent.slice(0, limit);
  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  const columns = [
    {
      accessorKey: 'breakdown_value',
      header: title.replace('Revenue by ', '').replace('Top ', ''),
      cell: ({ getValue }) => (
        <span className="font-medium capitalize">{getValue()}</span>
      ),
    },
    {
      accessorKey: 'revenue',
      header: 'Revenue',
      cell: ({ getValue }) => {
        const value = getValue();
        return value != null ? `$${Number(value).toLocaleString()}` : '$0';
      },
    },
    {
      accessorKey: 'revenue_percent',
      header: '%',
      cell: ({ getValue }) => {
        const value = getValue();
        return value != null ? `${Number(value).toFixed(1)}%` : '0.0%';
      },
    },
    {
      accessorKey: 'impressions',
      header: 'Impressions',
      cell: ({ getValue }) => {
        const value = getValue();
        return value != null ? Number(value).toLocaleString() : '0';
      },
    },
    {
      accessorKey: 'ecpm',
      header: 'eCPM',
      cell: ({ getValue }) => {
        const value = getValue();
        return value != null ? `$${Number(value).toFixed(2)}` : '$0.00';
      },
    },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{title}</CardTitle>
          <Button variant="ghost" size="sm" onClick={onToggleExpand}>
            {expanded ? (
              <>
                <ChevronUp className="w-4 h-4 mr-1" />
                Collapse
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4 mr-1" />
                View All
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {expanded ? (
          <DataTable data={dataWithPercent} columns={columns} pageSize={10} />
        ) : (
          <>
            {/* Chart */}
            {chartType === 'pie' && (
              <div className="mb-6 px-2 py-4">
                <ResponsiveContainer width="100%" height={280}>
                  <RechartsPieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                    <Pie
                      data={displayData}
                      dataKey="revenue"
                      nameKey="breakdown_value"
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      innerRadius={0}
                      label={({ revenue_percent, breakdown_value }) => {
                        // Only show label if percentage is >= 5% to avoid overlap
                        if (revenue_percent >= 5) {
                          // Truncate long names
                          const name = breakdown_value.length > 15
                            ? `${breakdown_value.substring(0, 15)}...`
                            : breakdown_value;
                          return `${name}: ${revenue_percent.toFixed(1)}%`;
                        }
                        return '';
                      }}
                      labelLine={false}
                    >
                      {displayData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [
                        value != null ? `$${Number(value).toLocaleString()}` : '$0',
                        name
                      ]}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--background))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                        padding: '8px 12px'
                      }}
                    />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      formatter={(value) => {
                        const item = displayData.find(d => d.breakdown_value === value);
                        if (item && item.revenue_percent < 5) {
                          // Show full info for small segments in legend
                          return `${value} (${item.revenue_percent.toFixed(1)}%)`;
                        }
                        return value;
                      }}
                      wrapperStyle={{ paddingTop: '16px' }}
                    />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </div>
            )}
            {chartType === 'bar' && (
              <div className="mb-6 px-2 py-4">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart
                    data={displayData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="breakdown_value"
                      stroke="#6b7280"
                      tick={{ fill: '#6b7280', fontSize: 11 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      stroke="#6b7280"
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      tickFormatter={(value) => `$${(value / 1000).toFixed(1)}K`}
                    />
                    <Tooltip
                      formatter={(value) => value != null ? `$${Number(value).toLocaleString()}` : '$0'}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--background))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                        padding: '8px 12px'
                      }}
                    />
                    <Bar dataKey="revenue" fill="#3b82f6" name="Revenue" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {/* List */}
            <div className="space-y-3">
              {displayData.map((item, index) => (
                <div key={item.breakdown_value} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">{index + 1}</Badge>
                    <span className="font-medium capitalize">{item.breakdown_value}</span>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold">${(item.revenue || 0).toLocaleString()}</div>
                    <div className="text-xs text-muted-foreground">{(item.revenue_percent || 0).toFixed(1)}%</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default BreakdownCard;
